import React, { useEffect, useState } from 'react';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Standalone Brix explainer — designed to be shared from Facebook posts and
 * found via Google. Reads to a non-technical audience and surfaces the
 * SlushBook value-prop (automatic Brix calculation per machine) prominently.
 *
 * Text is wired through i18next with Danish fallbacks so it works in DK
 * out-of-the-box even before translators add the keys for en/de/fr.
 */
const BrixInfoPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Lightweight SEO for share previews (Open Graph / Twitter / search).
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Hvad er Brix? — Den korte forklaring fra SlushBook';
    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return { el, created };
    };
    const description =
      'Brix er det vigtigste tal for, om en slush fryser rigtigt. Lær på 3 minutter hvad Brix betyder, hvorfor det er afgørende, og hvordan SlushBook gør det automatisk for dig.';
    const tracked = [
      ensureMeta('meta[name="description"]', { name: 'description', content: description }),
      ensureMeta('meta[property="og:title"]', { property: 'og:title', content: 'Hvad er Brix? — SlushBook' }),
      ensureMeta('meta[property="og:description"]', { property: 'og:description', content: description }),
      ensureMeta('meta[property="og:type"]', { property: 'og:type', content: 'article' }),
      ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' }),
    ];
    return () => {
      document.title = previousTitle;
      // Only remove meta tags we created in this mount; leave any pre-existing
      // tags as we found them.
      tracked.forEach(({ el, created }) => {
        if (created && el?.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#115DA8] to-[#1e88e5] text-white p-6 shadow-lg">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity"
          data-testid="brix-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('brix.back', 'Tilbage')}</span>
        </button>
        <h1 className="text-3xl font-bold" data-testid="brix-title">
          {t('brix.title', 'Hvad er Brix?')}
        </h1>
        <p className="text-white/85 mt-2 text-sm sm:text-base max-w-2xl">
          {t(
            'brix.lead',
            'Den korte forklaring — skrevet til dig, der bare vil have din slush til at lykkes hver gang.'
          )}
        </p>
      </div>

      <div className="px-4 py-6 space-y-8 max-w-3xl mx-auto">
        {/* 0 — Hero infographic. Designed to "stop the scroll" when shared as
            a link on Facebook or in chat groups, and to work as a standalone
            infographic if screenshotted. Fully responsive (mobile-first). */}
        <section
          data-testid="brix-hero-card"
          className="relative overflow-hidden rounded-2xl shadow-lg bg-gradient-to-br from-cyan-50 via-white to-amber-50 border border-gray-100 px-5 py-7 sm:px-10 sm:py-10"
        >
          {/* Decorative soft blob — kept aria-hidden */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 sm:w-64 sm:h-64 rounded-full bg-gradient-to-br from-cyan-200/40 to-amber-200/40 blur-2xl"
          />
          <div className="relative flex flex-col items-center text-center gap-3 sm:gap-4">
            {/* SlushBook mascot replaces the generic cup so the infographic
                visually ties back to the brand on shares. */}
            <img
              src="/slushbook-smiley.png"
              alt={t('brix.heroMascotAlt', 'SlushBook')}
              className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl shadow-md ring-2 ring-white"
              data-testid="brix-hero-mascot"
            />
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
              {t('brix.heroTitle', 'Den perfekte slush starter med den rigtige Brix')}
            </h2>
            <div className="text-sm sm:text-base text-gray-700 max-w-md leading-relaxed space-y-2">
              <p>
                {t(
                  'brix.heroSubtitle',
                  'Har du nogensinde prøvet at lave en slush, der blev for tynd eller aldrig rigtig frøs?'
                )}
              </p>
              <p className="text-gray-600">
                {t(
                  'brix.heroSubtitle2',
                  'En af de mest almindelige årsager er, at Brix-værdien ikke passer. Når Brix er korrekt, får du en slush med den rette balance mellem smag, konsistens og frysepunkt.'
                )}
              </p>
            </div>

            {/* Brix-value chip + caption */}
            <div className="mt-1 flex flex-col items-center gap-1">
              <div className="inline-flex items-center justify-center px-5 py-2 rounded-full bg-[#115DA8] text-white text-base sm:text-lg font-bold tracking-wide shadow-md">
                10 – 16 °Bx
              </div>
              <span className="text-xs sm:text-sm font-medium text-[#115DA8]/80 uppercase tracking-wider">
                {t('brix.heroChipCaption', 'Ideelt område for klassisk slush')}
              </span>
            </div>

            {/* 3 benefits — stack on mobile, row on sm+ */}
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-5 text-sm sm:text-base text-gray-800 w-full max-w-xl">
              <li className="flex items-center justify-center gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-green-700 text-xs font-bold">✓</span>
                <span>{t('brix.heroPoint1', 'Fryser korrekt')}</span>
              </li>
              <li className="flex items-center justify-center gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-green-700 text-xs font-bold">✓</span>
                <span>{t('brix.heroPoint2', 'Bliver dejlig cremet')}</span>
              </li>
              <li className="flex items-center justify-center gap-2">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-green-700 text-xs font-bold">✓</span>
                <span>{t('brix.heroPoint3', 'Smager bedst')}</span>
              </li>
            </ul>

            {/* Soft footer with SlushBook value-prop — discreet but always
                visible on shares. */}
            <div className="mt-4 pt-3 border-t border-gray-200/70 w-full max-w-xl">
              <p className="text-xs sm:text-sm text-gray-600 italic flex items-start sm:items-center justify-center gap-1.5 leading-relaxed">
                <span aria-hidden="true" className="mt-0.5 sm:mt-0">✨</span>
                <span>
                  {t(
                    'brix.heroFooter',
                    'Du behøver ikke selv beregne Brix. SlushBook finder automatisk den rigtige Brix-værdi ud fra din opskrift og din maskinstørrelse.'
                  )}
                </span>
              </p>
            </div>
          </div>
        </section>

        {/* 1 — Why does Brix matter? Lead with the user's pain, not the textbook definition. */}
        <section
          className="bg-white rounded-xl shadow-md p-6 space-y-3"
          data-testid="brix-section-why"
        >
          <p className="text-lg text-gray-900 leading-relaxed font-semibold">
            {t(
              'brix.whyHeadline',
              'Har du nogensinde prøvet en slush, der blev for tynd, for hård eller aldrig rigtig frøs?'
            )}
          </p>
          <p className="text-gray-700 leading-relaxed">
            {t('brix.whyBody0', 'Så skyldes det næsten altid, at Brix ikke var korrekt.')}
          </p>
          <p className="text-gray-700 leading-relaxed">
            {t(
              'brix.whyBody1a',
              'Brix fortæller, om blandingen har den rigtige balance, så slushen får den perfekte konsistens.'
            )}
          </p>
          <p className="text-gray-700 leading-relaxed">
            {t(
              'brix.whyBody1',
              'Hvis Brix er for lav, bliver slushen for tynd. Hvis den er for høj, kan den blive for tung eller have svært ved at fryse.'
            )}
          </p>
          <p className="text-gray-700 leading-relaxed">
            {t(
              'brix.whyBody2',
              'SlushBook beregner automatisk den korrekte Brix-værdi for dine opskrifter, så du ikke skal regne den ud selv.'
            )}
          </p>
          <div className="pt-2 border-t border-gray-100">
            <p className="text-sm text-gray-500 leading-relaxed">
              {t(
                'brix.whatItIs',
                'Brix er oprindeligt et mål for sukkerindholdet i en væske og angives i grader Brix (°Bx). 1 °Bx svarer cirka til 1 gram sukker pr. 100 gram væske. Slushmaskiner fryser ikke væsken til is — de rører konstant, så blandingen ligger lige på kanten af frysning. Derfor er Brix-tallet afgørende.'
              )}
            </p>
          </div>
        </section>

        {/* 2 — Visual: too-low / correct / too-high */}
        <section data-testid="brix-section-visual">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            {t('brix.visualTitle', 'Sådan ser forskellen ud')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Too low */}
            <div
              className="bg-white rounded-xl shadow-md p-5 border-t-4 border-cyan-300"
              data-testid="brix-card-low"
            >
              <div className="text-4xl mb-2">🧊</div>
              <h3 className="font-bold text-gray-900 mb-2">
                {t('brix.lowTitle', 'For lav Brix')}
              </h3>
              <ul className="text-sm text-gray-700 space-y-1.5">
                <li>• {t('brix.lowItem1', 'Slush bliver vandig')}</li>
                <li>• {t('brix.lowItem2', 'Fryser dårligt')}</li>
              </ul>
            </div>
            {/* Correct */}
            <div
              className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl shadow-md p-5 border-t-4 border-amber-400 ring-2 ring-amber-200/60"
              data-testid="brix-card-correct"
            >
              <div className="text-4xl mb-2">⚖️</div>
              <h3 className="font-bold text-gray-900 mb-2">
                {t('brix.correctTitle', 'Korrekt Brix')}
              </h3>
              <ul className="text-sm text-gray-800 space-y-1.5 font-medium">
                <li>• {t('brix.correctItem1', 'Cremet konsistens')}</li>
                <li>• {t('brix.correctItem2', 'Fryser korrekt')}</li>
                <li>• {t('brix.correctItem3', 'Bedste smag')}</li>
              </ul>
            </div>
            {/* Too high */}
            <div
              className="bg-white rounded-xl shadow-md p-5 border-t-4 border-orange-400"
              data-testid="brix-card-high"
            >
              <div className="text-4xl mb-2">🍯</div>
              <h3 className="font-bold text-gray-900 mb-2">
                {t('brix.highTitle', 'For høj Brix')}
              </h3>
              <ul className="text-sm text-gray-700 space-y-1.5">
                <li>• {t('brix.highItem1', 'Tung konsistens')}</li>
                <li>• {t('brix.highItem2', 'Kan have svært ved at fryse')}</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 3 — Why Brix and not just sugar */}
        <section
          className="bg-white rounded-xl shadow-md p-6 space-y-3"
          data-testid="brix-section-vs-sugar"
        >
          <h2 className="text-xl font-bold text-gray-800">
            {t('brix.vsSugarTitle', 'Hvorfor bruger vi Brix og ikke bare sukker?')}
          </h2>
          <p className="text-gray-700 leading-relaxed">
            {t(
              'brix.vsSugarIntro',
              'Det korte svar: sukker alene fortæller ikke hele historien om, hvor godt en slush fryser.'
            )}
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-[#115DA8] mt-1 font-bold">•</span>
              <span>
                {t(
                  'brix.vsSugarBullet1',
                  'Frugtsukker, glukose, fruktose og andre sukkerarter opfører sig forskelligt.'
                )}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#115DA8] mt-1 font-bold">•</span>
              <span>
                {t(
                  'brix.vsSugarBullet2',
                  'Alkohol sænker frysepunktet og kræver højere Brix for at slushen sætter sig.'
                )}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#115DA8] mt-1 font-bold">•</span>
              <span>
                {t(
                  'brix.vsSugarBullet3',
                  'Mælkesukker, syre og andre opløste stoffer påvirker også konsistensen.'
                )}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#115DA8] mt-1 font-bold">•</span>
              <span>
                {t(
                  'brix.vsSugarBullet4',
                  'Derfor bruger professionelle slushproducenter Brix som fælles mål — det samler alle disse faktorer i ét tal.'
                )}
              </span>
            </li>
          </ul>
        </section>

        {/* 3b — Beroligende tekst til nye brugere, placeret lige før det
            store value-prop kort. Holdt visuelt diskret så det fungerer som
            et "blødt mellemspil" snarere end et nyt design-element. */}
        <section
          className="rounded-xl bg-amber-50/60 border border-amber-100 px-5 py-4 text-center"
          data-testid="brix-section-newcomer"
        >
          <p className="text-sm sm:text-base text-gray-800">
            <span className="font-semibold">
              {t('brix.newcomerTitle', 'Ny i slush-verdenen?')}
            </span>{' '}
            {t(
              'brix.newcomerBody',
              'Bare rolig. Du vælger opskriften – SlushBook klarer beregningerne.'
            )}
          </p>
        </section>

        {/* 4 — What SlushBook does (highlighted value-prop) */}
        <section
          className="rounded-2xl shadow-lg p-6 sm:p-7 bg-gradient-to-br from-[#115DA8] to-[#1e88e5] text-white"
          data-testid="brix-section-slushbook"
        >
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex h-12 w-12 rounded-full bg-white/15 items-center justify-center text-2xl shrink-0">
              ✨
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">
                {t('brix.slushbookTitle', 'Du behøver ikke selv at beregne Brix')}
              </h2>
              <p className="text-white/90 leading-relaxed">
                {t(
                  'brix.slushbookBody',
                  'SlushBook hjælper dig automatisk med at beregne den korrekte Brix og tilpasse opskriften til din maskines størrelse. Du vælger en opskrift — vi sørger for, at den fryser rigtigt.'
                )}
              </p>
              <p className="text-white/90 leading-relaxed">
                {t(
                  'brix.slushbookBody2',
                  'Det betyder, at du kan koncentrere dig om smagen, mens SlushBook sørger for, at slushen fryser korrekt.'
                )}
              </p>
            </div>
          </div>
        </section>

        {/* 5 — Existing Brix table (kept as-is) */}
        <section className="bg-white rounded-xl shadow-md overflow-hidden" data-testid="brix-section-table">
          <div className="bg-gradient-to-r from-[#115DA8] to-[#1e88e5] text-white p-4">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              🧊 {t('brix.tableTitle', 'Ideelle Brix-værdier for slushice')}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b w-[30%]">{t('brix.tableHeaderType', 'Type')}</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 border-b w-[20%]">{t('brix.tableHeaderBrix', 'Anbefalet Brix')}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b w-[50%]">{t('brix.tableHeaderComment', 'Kommentar')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-800 font-medium">
                    {t('brix.typeClassic', 'Klassisk slushice')}<br />
                    <span className="text-sm text-gray-500">{t('brix.typeClassicSub', '(sirup + vand)')}</span>
                  </td>
                  <td className="px-6 py-4 bg-cyan-500 text-white text-center font-bold whitespace-nowrap">10 – 16 °Bx</td>
                  <td className="px-6 py-4 text-gray-700">{t('brix.typeClassicComment', 'Standard for de fleste maskiner.')}</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-800 font-medium">{t('brix.typeJuice', 'Juice-baseret')}</td>
                  <td className="px-6 py-4 bg-cyan-500 text-white text-center font-bold whitespace-nowrap">6 – 15 °Bx</td>
                  <td className="px-6 py-4 text-gray-700">{t('brix.typeJuiceComment', 'Indeholder naturligt frugtsukker.')}</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-800 font-medium">
                    {t('brix.typeSoda', 'Sodavands-slush')}<br />
                    <span className="text-sm text-gray-500">{t('brix.typeSodaSub', '(cola, Fanta)')}</span>
                  </td>
                  <td className="px-6 py-4 bg-cyan-500 text-white text-center font-bold whitespace-nowrap">8 – 13 °Bx</td>
                  <td className="px-6 py-4 text-gray-700">{t('brix.typeSodaComment', 'Skal ofte justeres, da sodavand er lidt for sukkerrig.')}</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-800 font-medium">{t('brix.typeSmoothie', 'Smoothie-/milkshake-slush')}</td>
                  <td className="px-6 py-4 bg-cyan-500 text-white text-center font-bold whitespace-nowrap">12 – 18 °Bx</td>
                  <td className="px-6 py-4 text-gray-700">{t('brix.typeSmoothieComment', 'Mælkesukker og frugt giver tykkere masse.')}</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-800 font-medium">
                    {t('brix.typeAlcohol', 'Alkohol-slush')}<br />
                    <span className="text-sm text-gray-500">{t('brix.typeAlcoholSub', '(fx rom, vodka)')}</span>
                  </td>
                  <td className="px-6 py-4 bg-cyan-500 text-white text-center font-bold whitespace-nowrap">12 – 20 °Bx</td>
                  <td className="px-6 py-4 text-gray-700">{t('brix.typeAlcoholComment', 'Alkohol sænker frysepunktet, så du skal hæve Brix for at få den til at sætte sig.')}</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-800 font-medium">{t('brix.typeCoffee', 'Kaffe-slush')}</td>
                  <td className="px-6 py-4 bg-cyan-500 text-white text-center font-bold whitespace-nowrap">8 – 16 °Bx</td>
                  <td className="px-6 py-4 text-gray-700">{t('brix.typeCoffeeComment', 'Espresso/kold kaffe blandet med sirup — spænder fra mildt sødet til dessert-agtigt.')}</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-800 font-medium">{t('brix.typeSport', 'Sport-/elektrolyt-slush')}</td>
                  <td className="px-6 py-4 bg-cyan-500 text-white text-center font-bold whitespace-nowrap">4 – 13 °Bx</td>
                  <td className="px-6 py-4 text-gray-700">{t('brix.typeSportComment', 'Lav-brix isotonisk drik — tilsæt elektrolytter og en anelse sødme, undgå kraftig sirup.')}</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-800 font-medium">{t('brix.typeSugarFree', 'Sukkerfri (light)')}</td>
                  <td className="px-6 py-4 bg-gray-400 text-white text-center font-bold whitespace-nowrap">0 – 4 °Bx</td>
                  <td className="px-6 py-4 text-gray-700">{t('brix.typeSugarFreeComment', 'Fryser for hårdt, så du skal tilsætte glycerin, frugtsaft eller fortykningsmiddel for at undgå iskrystaller.')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 6 — Pro tips (rewritten) */}
        <section
          className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl shadow-md p-6 border-l-4 border-amber-400"
          data-testid="brix-section-tips"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2">
            💡 {t('brix.proTipsTitle', 'Pro Tips')}
          </h3>
          <ul className="space-y-2 text-gray-700">
            {[
              t('brix.tip1', 'Start altid med den anbefalede Brix-værdi.'),
              t('brix.tip2', 'Justér lidt ad gangen, hvis konsistensen ikke passer.'),
              t('brix.tip3', 'For lav Brix giver en tynd slush.'),
              t('brix.tip4', 'For høj Brix kan gøre slushen tung eller forhindre korrekt frysning.'),
              t('brix.tip5', 'Forskellige maskiner kan kræve små justeringer.'),
            ].map((tip, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-amber-500 mt-1">•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* 7 — FAQ (SEO + share value) */}
        <FaqSection t={t} />

        {/* 8 — Trust / "why can I rely on SlushBook" closing section */}
        <section
          className="bg-white rounded-xl shadow-md p-6 space-y-3 border-l-4 border-[#115DA8]"
          data-testid="brix-section-trust"
        >
          <h2 className="text-xl font-bold text-gray-900">
            {t('brix.trustTitle', 'Hvorfor beregner SlushBook automatisk Brix?')}
          </h2>
          <p className="text-gray-700 leading-relaxed">
            {t(
              'brix.trustBody',
              'Når du vælger en opskrift i SlushBook, beregner systemet automatisk den korrekte Brix ud fra ingrediensernes sukkerindhold, alkohol, frugtsukker og andre opløste stoffer. Opskriften tilpasses samtidig din maskinstørrelse, så du får den bedst mulige konsistens.'
            )}
          </p>
        </section>
      </div>
    </div>
  );
};

const FAQ_ITEMS = [
  {
    key: 'brix.faq.q1',
    qDefault: 'Hvad betyder Brix helt præcist?',
    aDefault:
      'Brix er en måleenhed, der angiver mængden af opløst stof — primært sukker — i en væske. 1 °Bx svarer cirka til 1 gram sukker pr. 100 gram væske. I slush-verdenen bruger vi det som et samlet mål for, hvor "tung" en blanding er, fordi det styrer frysepunktet.',
  },
  {
    key: 'brix.faq.qSlushbook',
    qDefault: 'Hvorfor bruger SlushBook Brix?',
    aDefault:
      'Fordi Brix er det fælles mål, professionelle slushproducenter bruger til at sikre, at en blanding fryser korrekt. SlushBook beregner Brix automatisk for hver opskrift ud fra ingredienserne, så du får en velfungerende slush uden selv at skulle regne — uanset om du bruger sirup, juice, alkohol eller sukkerfri ingredienser.',
  },
  {
    key: 'brix.faq.q2',
    qDefault: 'Hvilken Brix-værdi er den rigtige til min slush?',
    aDefault:
      'De fleste klassiske slushice fungerer bedst mellem 12 og 14 °Bx. Juice-baserede slushes ligger typisk lavere (6–15), alkohol-slushes højere (12–20). Brug tabellen ovenfor som udgangspunkt — eller lad SlushBook gøre arbejdet for dig.',
  },
  {
    key: 'brix.faq.q3',
    qDefault: 'Hvorfor fryser min slush ikke ordentligt?',
    aDefault:
      'Den hyppigste årsag er, at Brix er enten for høj eller for lav. For lav giver en vandig slush, der ikke binder. For høj — typisk over 18 — gør blandingen så tung, at maskinen ikke kan fryse den. Tjek opskriftens Brix, og justér med lidt vand (for at sænke) eller sukker/sirup (for at hæve).',
  },
  {
    key: 'brix.faq.q4',
    qDefault: 'Skal jeg købe en Brix-måler (refraktometer)?',
    aDefault:
      'Det er ikke nødvendigt, hvis du bruger SlushBook. Vi beregner Brix automatisk ud fra dine ingredienser. En refraktometer er rart at have, hvis du eksperimenterer med egne opskrifter — men det er ikke et must.',
  },
  {
    key: 'brix.faq.q5',
    qDefault: 'Hvordan påvirker alkohol Brix?',
    aDefault:
      'Alkohol sænker frysepunktet. Derfor skal en alkohol-slush typisk have højere Brix (14–18) end en almindelig slush, for at maskinen overhovedet kan fryse blandingen. Jo højere alkoholprocent, jo højere Brix er som regel nødvendigt.',
  },
  {
    key: 'brix.faq.q6',
    qDefault: 'Hvad gør jeg, hvis min slush bliver for sød?',
    aDefault:
      'Det betyder ofte, at Brix er højere end nødvendigt. Du kan sænke sødmen ved at tilsætte lidt mere vand eller en mere syrlig komponent (citronsaft, æblesyre). Husk at justere lidt ad gangen og smage til.',
  },
  {
    key: 'brix.faq.q7',
    qDefault: 'Kan jeg lave sukkerfri slush?',
    aDefault:
      'Ja, men det kræver en lille trick. Uden sukker fryser blandingen for hårdt og bliver til iskrystaller. Tilsæt enten glycerin, en smule frugtjuice eller et fortykningsmiddel for at holde konsistensen cremet — eller brug en sukker-erstatning som Allulose, der opfører sig som sukker frysningsmæssigt.',
  },
  {
    key: 'brix.faq.q8',
    qDefault: 'Hvorfor skal jeg justere Brix mellem forskellige maskiner?',
    aDefault:
      'Slushmaskiner arbejder ved lidt forskellige temperaturer og omrøringshastigheder. En blanding, der fryser perfekt på én maskine, kan være lidt for tung eller tynd på en anden. Start med den anbefalede Brix og finpuds ud fra hvordan din egen maskine reagerer.',
  },
  {
    key: 'brix.faq.q9',
    qDefault: 'Kan jeg måle Brix derhjemme?',
    aDefault:
      'Ja. Professionelle bruger et refraktometer, men de fleste hjemmebrugere behøver det ikke, fordi SlushBook beregner Brix automatisk ud fra ingredienserne.',
  },
];

const FaqSection = ({ t }) => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section
      className="bg-white rounded-xl shadow-md p-6 space-y-3"
      data-testid="brix-section-faq"
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <h2 className="text-2xl font-bold text-gray-800 mb-2">
        {t('brix.faqTitle', 'Ofte stillede spørgsmål om Brix')}
      </h2>
      <div className="divide-y divide-gray-100">
        {FAQ_ITEMS.map((item, i) => {
          const open = openIdx === i;
          const question = t(item.key + '.q', item.qDefault);
          const answer = t(item.key + '.a', item.aDefault);
          return (
            <div
              key={item.key}
              className="py-2"
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
              data-testid={`brix-faq-item-${i + 1}`}
            >
              <button
                type="button"
                onClick={() => setOpenIdx(open ? -1 : i)}
                className="w-full flex items-center justify-between text-left py-2 gap-3"
                aria-expanded={open}
                data-testid={`brix-faq-toggle-${i + 1}`}
              >
                <span
                  className="font-semibold text-gray-900"
                  itemProp="name"
                >
                  {question}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-gray-500 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}
                />
              </button>
              {open && (
                <div
                  className="pb-3 text-gray-700 leading-relaxed text-sm"
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <span itemProp="text">{answer}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default BrixInfoPage;
